import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunk for sending email via your PHP endpoint
export const submitContactForm = createAsyncThunk(
  'contactForm/submitForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post('https://www.xtalball.com/api/sendmail.php', formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log(response.data)
      // Check if response contains { "ok": true }
      if (response.data && response.data.ok === true) {
        return { success: true };
      } else {
        return rejectWithValue(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      // Handle network errors or other issues
      if (error.response) {
        // The request was made and the server responded with a status code
        return rejectWithValue(error.response.data.message || 'Server error');
      } else if (error.request) {
        // The request was made but no response was received
        return rejectWithValue('No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request
        return rejectWithValue(error.message || 'Failed to send message');
      }
    }
  }
);

// Initial state
const initialState = {
  // Form data
  formData: {
    // Personal Information
    name: '',
    position: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    
    // Additional Requirements
    specialRequirements: '',
    //timeline: '',
    //budget: '',
    partNumber: 'partNumberxxx',
    
    // Message
    message: ''
  },
  
  // UI state
  submitted: false,
  submitting: false,
  submitSuccess: false,
  submitError: null,
  
  // Form validation
  errors: {},
  
};

// Create slice
const contactFormSlice = createSlice({
  name: 'contactForm',
  initialState,
  reducers: {
    // Update form field
    updateField: (state, action) => {
      const { name, value } = action.payload;
      state.formData[name] = value;
      // Clear error for this field if it exists
      if (state.errors[name]) {
        delete state.errors[name];
      }
    },
    
    // Update multiple fields at once
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    
    // Reset form to initial state
    resetForm: (state) => {
      state.formData = initialState.formData;
      state.submitted = false;
      state.submitSuccess = false;
      state.submitError = null;
      state.errors = {};
    },
    
    // Set submitted status
    setSubmitted: (state, action) => {
      state.submitted = action.payload;
    },
    
    // Set form errors
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    
    // Clear form errors
    clearErrors: (state) => {
      state.errors = {};
    },
    
  },
  extraReducers: (builder) => {
    builder
      // Submit form pending
      .addCase(submitContactForm.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      // Submit form fulfilled
      .addCase(submitContactForm.fulfilled, (state) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.submitted = true;
        // Optionally reset form data after successful submit
        // state.formData = initialState.formData;
      })
      // Submit form rejected
      .addCase(submitContactForm.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to submit form';
        state.submitSuccess = false;
      });
  }
});

// Export actions
export const { 
  updateField, 
  updateFormData, 
  resetForm, 
  setSubmitted, 
  setErrors, 
  clearErrors,
} = contactFormSlice.actions;

// ============================================
// SELECTORS
// ============================================

// Basic form data selectors
export const selectFormData = (state) => state.contactForm.formData;
export const selectPersonalInfo = (state) => {
  const { name, position, company, email, phone, country } = state.contactForm.formData;
  return { name, position, company, email, phone, country };
};
export const selectTechnicalRequirements = (state) => {
  const { frequency, stability, voltage, temperatureRange } = state.contactForm.formData;
  return { frequency, stability, voltage, temperatureRange };
};
// export const selectAdditionalRequirements = (state) => {
//   const { specialRequirements, timeline, budget } = state.contactForm.formData;
//   return { specialRequirements, timeline, budget };
// };
export const selectAdditionalRequirements = (state) => {
  const { partNumber } = state.contactForm.formData;
  return {partNumber};
};

export const selectMessage = (state) => state.contactForm.formData.message;

// Individual field selectors
export const selectName = (state) => state.contactForm.formData.name;
export const selectPosition = (state) => state.contactForm.formData.position;
export const selectCompany = (state) => state.contactForm.formData.company;
export const selectEmail = (state) => state.contactForm.formData.email;
export const selectPhone = (state) => state.contactForm.formData.phone;
export const selectCountry = (state) => state.contactForm.formData.country;
export const selectFrequency = (state) => state.contactForm.formData.frequency;
export const selectStability = (state) => state.contactForm.formData.stability;
export const selectVoltage = (state) => state.contactForm.formData.voltage;
export const selectTemperatureRange = (state) => state.contactForm.formData.temperatureRange;
export const selectSpecialRequirements = (state) => state.contactForm.formData.specialRequirements;
//export const selectTimeline = (state) => state.contactForm.formData.timeline;
//export const selectBudget = (state) => state.contactForm.formData.budget;
export const selectPartNumber = (state) => state.contactForm.formData.partNumber;
export const selectFormMessage = (state) => state.contactForm.formData.message;

// UI state selectors
export const selectSubmitted = (state) => state.contactForm.submitted;
export const selectSubmitting = (state) => state.contactForm.submitting;
export const selectSubmitSuccess = (state) => state.contactForm.submitSuccess;
export const selectSubmitError = (state) => state.contactForm.submitError;

// Validation selectors
export const selectFormErrors = (state) => state.contactForm.errors;
export const selectFieldError = (field) => (state) => state.contactForm.errors[field];

// Combined selectors for form validation
export const selectIsFormValid = (state) => {
  const { name, company, email, country } = state.contactForm.formData;
  const hasRequiredFields = name && company && email && country;
  const hasNoErrors = Object.keys(state.contactForm.errors).length === 0;
  return hasRequiredFields && hasNoErrors;
};

// Selector to check if a field has error and show it
export const selectFieldWithError = (field) => (state) => ({
  value: state.contactForm.formData[field],
  error: state.contactForm.errors[field]
});

// Selector to get form data for submission (with computed fields)
export const selectFormDataForSubmission = (state) => {
  const { formData, productSpecs } = state.contactForm;
  return {
    ...formData,
    submittedAt: new Date().toISOString(),
    productModel: productSpecs.model,
    // Add any computed fields here
    fullName: `${formData.name}`,
    inquiryType: 'OCXO Product Inquiry'
  };
};

export default contactFormSlice.reducer;